import React, { useEffect, useState } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import momentPlugin from '@fullcalendar/moment';
import interactionPlugin from '@fullcalendar/interaction';

import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { randomId, specialOfferImg } from '@dineoutltd/utilities';
import { Button } from '@dineoutltd/components';
import PropTypes from 'prop-types';
import * as styles from './style.module.scss';
import * as styles2 from '../BookRestaurantForm/style.module.scss';
import BookRestaurantForm from '../BookRestaurantForm';
import PoweredBy from '../PoweredBy';
import RequestService from '../../networking/RequestService';
import Place from '../../networking/resources/Place';
import ServicesSection from '../ServicesSection';
import ReviewsSection from '../ReviewsSection';
import MenuSection from '../MenuSection';

function EventContent({ event, eventType }) {
  return (
    <div className={`${styles['event-block']} ${eventType === 'calendar' ? styles['event-calendar'] : styles['event-horizontal']}`}>
      <img src={specialOfferImg(event.extendedProps.id, event.extendedProps.thumb, 577, 420)} alt="" />
      <div className={styles['event-information']}>
        <div>
          <h2>{event.title}</h2>
          {event.extendedProps.startHour && (
            <p className={styles['event-hours']}>
              {event.extendedProps.startHour.slice(0, -3)}
              {' - '}
              {event.extendedProps.endHour.slice(0, -3)}
            </p>
          )}
          {eventType !== 'calendar' && (
            <p>{event.description}</p>
          )}
        </div>
      </div>

    </div>
  );
}

EventContent.propTypes = {
  event: PropTypes.oneOfType([PropTypes.object]).isRequired,
  eventType: PropTypes.string,
};

EventContent.defaultProps = {
  eventType: 'calendar',
};

export default function CalendarEvents({
  restaurantInfo, restaurantId, lang, services, menu, reviews,
}) {
  const { t, i18n } = useTranslation();
  const [showForm, setShowForm] = useState(false);
  const [date, setDate] = useState();
  const [events, setEvents] = useState([]);

  useEffect(() => {
    if (!restaurantInfo.id) return;
    (new RequestService('client/special-offers/place'))
      .setParams({
        place_id: restaurantInfo.id,
        locale: i18n.language,
      })
      .send()
      .then((response) => {
        const currentEvents = response.data
          .map((specialOffer) => specialOffer.intervals.map((interval) => ({
            id: randomId(),
            title: specialOffer.name,
            description: specialOffer.description,
            start: `${interval.date}T${interval.start}`,
            end: `${interval.date}T${interval.end}`,
            allDay: false,
            extendedProps: {
              id: specialOffer.id,
              startHour: interval.start,
              endHour: interval.end,
              thumb: specialOffer.thumb,
            },
          }))).flat();

        currentEvents.push({
          id: 'button',
          daysOfWeek: [0, 1, 2, 3, 4, 5, 6],
          title: 'button',
        });

        setEvents(currentEvents);
      })
      .catch(() => null);
  }, [restaurantInfo, i18n.language]);

  const days = [t('sun'), t('mon'), t('tue'), t('wed'), t('thu'), t('fri'), t('sat')];

  const eventsByDate = events.filter((event) => {
    if (event.id === 'button') return false;
    return moment(event.start).isSame(date, 'day');
  });

  eventsByDate.sort((a, b) => moment(a.start).diff(moment(b.start)));

  return (
    <>

      {showForm ? (
        <div className="lg-two-cols">
          <div className="col">
            <BookRestaurantForm
              restaurantId={restaurantId}
              restaurantInfo={restaurantInfo}
              lang={lang}
              staticDate={date}
              setShowForm={setShowForm}
              events={eventsByDate}
            />
          </div>
          {(eventsByDate.length > 0 || services || reviews || menu) && (
          <div className="col">
            {eventsByDate.length > 0 && (
            <>
              <h2 className="section-title">
                <strong>{restaurantInfo.name}</strong>
                {` ${t('pageBookings:offers_of_the_day')} `}
              </h2>
              {eventsByDate.map((event) => (
                <EventContent key={event.id} event={event} eventType="horizontal" />
              ))}
            </>
            )}
            {services && (
              <ServicesSection
                restaurantId={restaurantId}
                name={restaurantInfo.name}
                slug={restaurantInfo.slug}
              />
            )}
            {reviews && (
              <ReviewsSection
                restaurantId={restaurantId}
                name={restaurantInfo.name}
                restaurantInfo={restaurantInfo}
              />
            )}
            {menu && (
              <MenuSection
                info={restaurantInfo}
                restaurantId={restaurantId}
                name={restaurantInfo.name}
              />
            )}
          </div>
          )}
        </div>
      ) : (
        <div className={styles2['booking-info-block']}>
          <PoweredBy restaurantInfo={restaurantInfo} lang={lang} />
          <FullCalendar
            height="auto"
            nextDayThreshold="06:00:00"
            locale={i18n.language}
            plugins={[dayGridPlugin, momentPlugin, interactionPlugin]}
            initialView="dayGridMonth"
            firstDay={1}
            validRange={(nowDate) => ({ start: nowDate })}
            events={events}
            dayMaxEvents
            views={
              {
                dayGridMonth: {
                  dayMaxEvents: 3,
                },
              }
            }
            eventClick={({ event }) => {
              setShowForm(true);
              setDate(moment(event.start));
            }}
            dateClick={(info) => {
              setShowForm(true);
              setDate(moment(info.date));
            }}
            headerToolbar={{
              left: 'title',
              center: '',
              right: 'prev,next',
            }}
            dayCellContent={(arg) => (
              <>
                {arg.dayNumberText}
                <span className={styles['month-day']}>
                  {'.'}
                  {arg.date.getMonth() + 1}
                  {' '}
                  {days[arg.date.getDay()]}
                </span>
              </>
            )}
            eventContent={(arg) => {
              if (arg.event.id === 'button') {
                return <Button type="outline" fullWidth text={t('book_now')} onClick={() => {}} />;
              }
              return <EventContent event={arg.event} />;
            }}
            moreLinkContent={(arg) => `+${arg.num} ${t('more')}`}
          />
        </div>
      )}
    </>
  );
}

CalendarEvents.propTypes = {
  restaurantInfo: PropTypes.oneOfType([PropTypes.object, PropTypes.instanceOf(Place)]).isRequired,
  restaurantId: PropTypes.string.isRequired,
  lang: PropTypes.string.isRequired,
  services: PropTypes.bool,
  menu: PropTypes.bool,
  reviews: PropTypes.bool,
};

CalendarEvents.defaultProps = {
  services: false,
  menu: false,
  reviews: false,
};
